/**
 * Where "Keep talking" can take someone after a live: the chat that carries on (a room's Everyone, or a circle),
 * who is still talking in it, and the words for that. A quick live has no such chat, so only Message Maya shows.
 */
import { useEffect, useState } from "react";
import type { TalkFace, TalkMessage } from "../../../shared/talk";
import { api } from "../../lib/api";

export interface KeepTalking {
  /** The place on his page, as `?at=` names it. */
  at: string;
  noun: "room" | "circle";
  faces: TalkFace[];
  others: number;
}

/** "Grace, Aisha and 10 others", or null when nobody else is talking. */
export function talkingWords(names: readonly string[], others: number): string | null {
  const parts = [...names, ...(others > 0 ? [`${others} ${others === 1 ? "other" : "others"}`] : [])];
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** The people who wrote in a room's Everyone, the viewer and the host left out, in the order they last wrote. */
export function voicesIn(messages: readonly TalkMessage[]): { faces: TalkFace[]; others: number } {
  const seen = new Map<string, TalkFace>();
  for (const m of [...messages].reverse()) {
    if (m.who.kind !== "member" || m.who.me || seen.has(m.who.registrantId)) continue;
    seen.set(m.who.registrantId, { name: m.who.name, photoUrl: m.who.photoUrl });
  }
  const all = [...seen.values()];
  return { faces: all.slice(0, 3), others: Math.max(0, all.length - 2) };
}

/** What Keep talking offers for this live; null while it loads, and for a live with no chat to carry on in. */
export function useKeepTalking(token: string, webinarId: string, circleId: string | undefined, viewerFirst: string): KeepTalking | null {
  const [found, setFound] = useState<KeepTalking | null>(null);
  useEffect(() => {
    let alive = true;
    const load = async (): Promise<KeepTalking | null> => {
      if (circleId) {
        const { circle, members } = await api.guestTalk.circle(token, circleId);
        const others = members.filter((f) => f.name.split(" ")[0] !== viewerFirst);
        return { at: `circle:${circleId}`, noun: "circle", faces: others.slice(0, 3), others: Math.max(0, circle.members - 1 - Math.min(2, others.length)) };
      }
      const { rooms } = await api.guestTalk.get(token);
      if (!rooms.some((r) => r.webinarId === webinarId && r.everyoneChat)) return null;
      const { messages } = await api.guestTalk.everyone(token, webinarId);
      return { at: `everyone:${webinarId}`, noun: "room", ...voicesIn(messages) };
    };
    load()
      .then((k) => alive && setFound(k))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [token, webinarId, circleId, viewerFirst]);
  return found;
}
